import { CourseStatus, CourseType, URLS } from '@/lib/constants';

/**
 * WordPress course/lesson links (LearnDash permalinks) share the app's URL
 * shape: `/courses/<course-slug>/` and `/courses/<course-slug>/<lesson-slug>/`.
 * Links to them are what members paste into The Hang. On the web, WordPress
 * redirects logged-in members with access to app.openstudiojazz.com for
 * courses flagged `is_josephine`; inside the app WebView that round-trip would
 * land in Safari, so these helpers resolve the in-app route directly.
 */

const WP_HOSTS = new Set(['www.openstudiojazz.com', 'openstudiojazz.com']);
try {
  WP_HOSTS.add(new URL(URLS.WORDPRESS_BASE).hostname.toLowerCase());
} catch {
  // keep defaults
}

// One or two slug segments, optional trailing slash. Deeper LearnDash paths
// (topics, quizzes) and app-only shapes (/collections/, /skillpath/) are
// deliberately not matched.
const WP_COURSE_PATH_RE = /^\/courses\/([A-Za-z0-9\-_]+)(?:\/([A-Za-z0-9\-_]+))?\/?$/;
const APP_RESERVED_SEGMENTS = new Set(['skillpath', 'collections']);

export type WpCourseLink = {
  courseSlug: string;
  lessonSlug: string | null;
  /** Original pathname (autologin `next` for the browser fallback). */
  pathname: string;
  /** Query + hash to carry over to the in-app route. */
  suffix: string;
};

/** Parse an absolute WordPress course/lesson URL, or null for anything else. */
export function parseWpCourseUrl(raw: string | null | undefined): WpCourseLink | null {
  if (!raw || !/^https?:\/\//i.test(raw)) return null;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (!WP_HOSTS.has(u.hostname.toLowerCase())) return null;
  const m = WP_COURSE_PATH_RE.exec(u.pathname);
  if (!m) return null;
  const courseSlug = m[1];
  const lessonSlug = m[2] ?? null;
  if (APP_RESERVED_SEGMENTS.has(courseSlug) || (lessonSlug && APP_RESERVED_SEGMENTS.has(lessonSlug))) {
    return null;
  }
  return { courseSlug, lessonSlug, pathname: u.pathname, suffix: `${u.search}${u.hash}` };
}

export type WpCourseRouteDeps = {
  /** Course by slug; resolves null (or throws) when it doesn't exist. */
  getCourseBySlug: (slug: string) => Promise<{
    id: number;
    status?: number | null;
    type?: number | null;
    redirect_url?: string | null;
  } | null>;
  /** Whether a published lesson with this slug exists in the course. */
  publishedLessonExists: (courseId: number, lessonSlug: string) => Promise<boolean>;
};

/**
 * In-app route for a parsed WordPress course/lesson link, or null when the
 * content isn't available in the app (caller falls back to the browser):
 *  - redirect-type course (e.g. The Harmony Games): course link → its
 *    redirect_url; lesson link → the same path, which the lesson RPC maps to
 *    the canonical lesson (verified for the-harmony-games/the-family).
 *  - published course: course link → /courses/<slug>; lesson link → the
 *    lesson route, only if that lesson exists and is published.
 *  - draft / unknown course, or any lookup failure → null.
 */
export async function resolveWpCourseRoute(
  link: WpCourseLink,
  deps: WpCourseRouteDeps,
): Promise<string | null> {
  let course: Awaited<ReturnType<WpCourseRouteDeps['getCourseBySlug']>>;
  try {
    course = await deps.getCourseBySlug(link.courseSlug);
  } catch {
    return null;
  }
  if (!course) return null;

  if (course.type === CourseType.Redirect && course.redirect_url) {
    if (link.lessonSlug) {
      return `/courses/${link.courseSlug}/${link.lessonSlug}${link.suffix}`;
    }
    return course.redirect_url;
  }

  if (course.status !== CourseStatus.Published) return null;

  if (!link.lessonSlug) {
    return `/courses/${link.courseSlug}${link.suffix}`;
  }
  try {
    if (!(await deps.publishedLessonExists(course.id, link.lessonSlug))) return null;
  } catch {
    return null;
  }
  return `/courses/${link.courseSlug}/${link.lessonSlug}${link.suffix}`;
}
